import { TranslocoPipe, provideTranslocoScope } from '@jsverse/transloco';
import { Component, Input, OnChanges } from '@angular/core';

interface RevenuePaymentAmount {
  idDocumento: string;
  importoAssegnato: number;
  nuovoIncassoAttivo: boolean;
}

@Component({
  providers: [provideTranslocoScope('receipts')],
  imports: [TranslocoPipe],
  selector: 'rev-receipts-payment-amount-cell',
  template: `
    <div
      class="amount-field"
      [class.amount-field-disabled]="!itemData.nuovoIncassoAttivo"
      (click)="$event.stopPropagation()"
    >
      <span aria-hidden="true">€</span>
      <input
        type="text"
        inputmode="decimal"
        [value]="displayValue"
        [disabled]="!itemData.nuovoIncassoAttivo"
        [attr.aria-label]="(('receipts.ui.importoAssegnatoPer' | transloco) + &quot; &quot;) + itemData.idDocumento"
        (focus)="prepareInput($event)"
        (input)="updateAmount($event)"
        (blur)="commitAmount()"
        (keydown)="validateKey($event)"
      />
      @if (itemData.importoAssegnato > 0) {
        <button
          type="button"
          class="clear-amount-button"
          [disabled]="!itemData.nuovoIncassoAttivo"
          [attr.aria-label]="(('receipts.ui.svuotaImportoAssegnatoPer' | transloco) + &quot; &quot;) + itemData.idDocumento"
          (mousedown)="$event.preventDefault()"
          (click)="clearAmount($event)"
        >
          <i class="fa-regular fa-xmark" aria-hidden="true"></i>
        </button>
      }
    </div>
  `,
  styles: `
    :host {
      display: flex;
      justify-content: flex-end;
    }

    .amount-field {
      display: flex;
      align-items: center;
      gap: var(--mat-sys-spacing-4);
      width: 144px;
      height: 32px;
      padding: 0 var(--mat-sys-spacing-8);
      border: 1px solid var(--mat-sys-primary);
      border-radius: var(--mat-sys-corner-medium);
      color: var(--mat-sys-primary);
      background: var(--mat-sys-surface);
      box-sizing: border-box;
    }

    .amount-field-disabled {
      border-color: color-mix(in srgb, var(--mat-sys-on-surface) 12%, transparent);
      color: color-mix(in srgb, var(--mat-sys-on-surface) 38%, transparent);
      background-color: color-mix(in srgb, var(--mat-sys-on-surface) 4%, var(--mat-sys-surface));
      cursor: not-allowed;
    }

    input {
      width: 100%;
      min-width: 0;
      padding: 0;
      border: 0;
      outline: 0;
      color: inherit;
      background: transparent;
      font: inherit;
      text-align: right;
    }

    input:disabled {
      cursor: not-allowed;
    }

    .clear-amount-button {
      display: inline-flex;
      flex: 0 0 20px;
      align-items: center;
      justify-content: center;
      width: 20px;
      height: 20px;
      margin-left: var(--mat-sys-spacing-8);
      padding: 0;
      border: 0;
      color: var(--mat-sys-on-surface-variant);
      background: transparent;
      cursor: pointer;
    }

    .clear-amount-button:disabled {
      color: color-mix(in srgb, var(--mat-sys-on-surface) 38%, transparent);
      cursor: not-allowed;
    }

    .clear-amount-button i {
      font-size: 16px;
      line-height: 16px;
    }
  `,
})
export class receiptsPaymentAmountCell implements OnChanges {
  @Input({ required: true }) itemData!: RevenuePaymentAmount;
  @Input() infoColumn: unknown;
  @Input() _reloadData?: { emit: () => void };

  displayValue = '0,00';

  ngOnChanges(): void {
    this.formatAmount();
  }

  prepareInput(event: FocusEvent): void {
    if (this.itemData.importoAssegnato !== 0) {
      return;
    }

    this.displayValue = '';
    (event.target as HTMLInputElement).value = '';
  }

  validateKey(event: KeyboardEvent): void {
    event.stopPropagation();

    if (event.ctrlKey || event.metaKey) {
      return;
    }

    const allowedKeys = [
      'Backspace', 'Delete', 'Tab', 'Enter',
      'ArrowLeft', 'ArrowRight', 'Home', 'End',
    ];

    if (allowedKeys.includes(event.key) || /^\d$/.test(event.key)) {
      return;
    }

    if ((event.key === ',' || event.key === '.') && !this.displayValue.includes(',')) {
      return;
    }

    event.preventDefault();
  }

  updateAmount(event: Event): void {
    const input = event.target as HTMLInputElement;
    const normalizedValue = input.value.replace('.', ',');
    const [integerPart, ...decimalParts] = normalizedValue.split(',');
    const sanitizedValue = integerPart.replace(/\D/g, '')
      + (decimalParts.length ? `,${decimalParts.join('').replace(/\D/g, '')}` : '');

    this.displayValue = sanitizedValue;
    input.value = sanitizedValue;

    if (!sanitizedValue) {
      this.itemData.importoAssegnato = 0;
      return;
    }

    const amount = Number(sanitizedValue.replace(',', '.'));

    if (Number.isFinite(amount) && amount >= 0) {
      this.itemData.importoAssegnato = amount;
    }
  }

  commitAmount(): void {
    this._reloadData?.emit();
  }

  clearAmount(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.itemData.importoAssegnato = 0;
    this.displayValue = '0,00';
    this._reloadData?.emit();
  }

  formatAmount(): void {
    const amount = this.itemData?.importoAssegnato ?? 0;
    this.displayValue = amount.toFixed(2).replace('.', ',');
  }
}
